import ConfirmDialog from "components/ConfirmDialog"
import PageLoader from "components/PageLoader"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useNavigate } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import {
  Pencil,
  Plus,
  Trash,
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  ShieldCheck,
  User,
} from "lucide-react"
import { useAlert } from "providers/AlertProvider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface UserItem {
  username: string
  sub: string
  groups: string[]
}

const PAGE_SIZE_OPTIONS = [30, 50, 100] as const

const GroupBadge = ({ group }: { group: string }) => {
  const isAdmin = group.toLowerCase() === "admin"
  return (
    <Badge variant={isAdmin ? "default" : "secondary"} className="gap-1 text-xs">
      {isAdmin ? <ShieldCheck className="size-3" /> : <Shield className="size-3" />}
      {group}
    </Badge>
  )
}

export const UsersPage = () => {
  const navigate = useNavigate()
  const service = getTintVTOService()
  const [users, setUsers] = useState<UserItem[]>([])
  const [isLoading, setLoading] = useState(false)
  const { t } = useTranslation()
  const [userToDelete, setUserToDelete] = useState<string | null>(null)
  const userDetails = service.userDetails()
  const { showAlert } = useAlert()

  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<number>(30)

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true)
      const {
        data: { listUsers },
      } = await service.listUsers()
      setUsers(listUsers)
      setLoading(false)
    }
    void loadUsers()
  }, [service])

  const filtered = useMemo(() => {
    if (!search.trim()) return users
    const q = search.toLowerCase()
    return users.filter(
      (u) =>
        u.username?.toLowerCase().includes(q) ||
        u.sub?.toLowerCase().includes(q) ||
        u.groups?.some((g) => g.toLowerCase().includes(q))
    )
  }, [users, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePageIndex = Math.min(page, totalPages - 1)
  const paginated = filtered.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize)

  return (
    <TooltipProvider delayDuration={300}>
      <ConfirmDialog
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={async () => {
          if (!userToDelete) return
          await service.deleteUser(userToDelete)
          setUserToDelete(null)
          showAlert({
            error: false,
            text: t("usersPage.userDeleted", { username: userToDelete }),
          })
          const {
            data: { listUsers },
          } = await service.listUsers()
          setUsers(listUsers)
        }}
        text={t("usersPage.deleteConfirmation", { username: userToDelete })}
      />

      <div className="p-6" data-testid="users-page">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="users-title">
              {t("usersPage.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("usersPage.usersFound", { count: filtered.length })}
            </p>
          </div>
          <Button size="sm" asChild>
            <RouterLink to="/users/add" data-testid="users-add">
              <Plus className="mr-1.5 size-3.5" />
              {t("usersPage.addUser")}
            </RouterLink>
          </Button>
        </div>

        {/* Search + page size */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="users-search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(0)
              }}
              placeholder="Search by username, sub, or group..."
              className="pl-9"
            />
          </div>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v))
              setPage(0)
            }}
          >
            <SelectTrigger className="w-[100px]" data-testid="users-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {isLoading ? (
          <PageLoader />
        ) : paginated.length > 0 ? (
          <Card data-testid="users-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-medium">{t("usersPage.username")}</TableHead>
                  <TableHead className="font-medium">{t("usersPage.sub")}</TableHead>
                  <TableHead className="font-medium">{t("usersPage.groups")}</TableHead>
                  <TableHead className="w-[100px] text-right font-medium">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((user: UserItem) => (
                  <TableRow key={user.sub} data-testid="user-row" data-user-sub={user.sub}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex size-7 items-center justify-center rounded-full bg-muted">
                          <User className="size-3.5 text-muted-foreground" />
                        </div>
                        <span className="font-medium">{user.username}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {user.sub}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        {user.groups?.map((group) => (
                          <GroupBadge key={group} group={group} />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={() =>
                                navigate(
                                  `/users/edit?username=${user.username}&group=${user.groups[0]}`
                                )
                              }
                              data-testid="user-edit"
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            <p>{t("usersPage.edit")}</p>
                          </TooltipContent>
                        </Tooltip>
                        {userDetails.username !== user.username && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setUserToDelete(user.username)}
                                data-testid="user-delete"
                              >
                                <Trash className="size-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>{t("usersPage.delete")}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  {safePageIndex * pageSize + 1}–
                  {Math.min((safePageIndex + 1) * pageSize, filtered.length)} of {filtered.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-8"
                    disabled={safePageIndex === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <span className="min-w-[80px] text-center text-sm text-muted-foreground">
                    {safePageIndex + 1} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-8"
                    disabled={safePageIndex >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ) : (
          <Card className="border-dashed" data-testid="users-empty-state">
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 py-12">
              <User className="size-8 text-muted-foreground/50" />
              <p className="font-medium">No users found</p>
              <p className="text-sm text-muted-foreground">
                {search ? "Try adjusting your search" : "Add your first user to get started"}
              </p>
            </div>
          </Card>
        )}
      </div>
    </TooltipProvider>
  )
}
